import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Table, Checkbox, Space } from "antd";
import SubHeader from "../../components/SubHeader/SubHeader";
import InputField from "../../components/InputField/InputField";
import SelectInput from "../../components/SelectInput/SelectInput";
import DateField from "../../components/DateField/DateField";
import ImageUploadField from "../../components/ImageUploadField/ImageUploadField";
import Loader from "../../components/Loader/Loader";
import ButtonComponent from "../../components/ButtonComponent/ButtonComponent";
import { errorToast, successToast } from "../../services/ToastHelper";
import { postApi, getApi } from "../../utils/apiService";
import {
    ADD_STAFF,
    UPDATE_STAFF,
    GET_STAFF_BY_ID,
    GET_ALL_MODULES,
    GET_ALL_BRANCH
} from "../../utils/apiPath";
import dayjs from "dayjs";
import "./ManageTeam.scss";

const AddEditStaff = () => {
    const { teamId } = useParams();
    const navigate = useNavigate();
    const mode = teamId ? "edit" : "add";

    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [modulesList, setModulesList] = useState([]);
    const [branchesList, setBranchesList] = useState([]);

    const [formData, setFormData] = useState({
        name: "",
        designation: "",
        profileImage: "",
        emailId: "",
        phoneNumber: "",
        department: "",
        dateOfBirth: null,
        gender: null,
        gradeIds: [],
        branchIds: [],
        permissions: []
    });
    const [errors, setErrors] = useState({});

    const fetchModules = async () => {
        const res = await getApi(GET_ALL_MODULES);
        const data = Array.isArray(res) ? res : (res?.data || []);
        setModulesList(data);

        if (mode === "add") {
            const initialPermissions = data.map(m => ({
                sectionId: m.value,
                label: m.label,
                canView: false,
                canAdd: false,
                canEdit: false,
                canDelete: false
            }));
            setFormData(prev => ({ ...prev, permissions: initialPermissions }));
        }
    };

    const fetchBranches = async () => {
        const { statusCode, data } = await getApi(GET_ALL_BRANCH);
        if (statusCode === 200) {
            const list = Array.isArray(data) ? data : (data?.data || []);
            setBranchesList(list.map(b => ({
                label: b.name,
                value: b.branchId
            })));
        }
    };

    const fetchStaffDetails = async () => {
        const { statusCode, data, message } = await postApi(GET_STAFF_BY_ID, { teamId });
        if (statusCode === 200) {
            setFormData({
                name: data.name || "",
                designation: data.designation || "",
                profileImage: data.profileImage || "",
                emailId: data.emailId || "",
                phoneNumber: data.phoneNumber || "",
                department: data.department || "",
                dateOfBirth: data.dateOfBirth ? dayjs(data.dateOfBirth) : null,
                gender: data.gender || null,
                gradeIds: data.gradeIds || [],
                branchIds: data.branches?.map(b => b.branchId) || data.branchIds || [],
                permissions: data.permissions || []
            });
        } else {
            errorToast(message);
        }
        setIsLoading(false);
    };

    useEffect(() => {
        const init = async () => {
            setIsLoading(true);
            await Promise.all([
                fetchModules(),
                fetchBranches(),
                mode === "edit" ? fetchStaffDetails() : Promise.resolve()
            ]);
            setIsLoading(false);
        };
        init();
    }, [teamId]);

    // Update permissions with labels once both are loaded
    useEffect(() => {
        if (modulesList.length > 0 && formData.permissions.length > 0 && mode === "edit") {
            // Check if labels are already mapped to avoid infinite loop
            const alreadyMapped = formData.permissions.every(p => p.label);
            if (alreadyMapped) return;

            const mappedPermissions = modulesList.map(m => {
                const existing = formData.permissions.find(p => p.sectionId === m.value);
                return {
                    sectionId: m.value,
                    label: m.label,
                    canView: existing?.canView || false,
                    canAdd: existing?.canAdd || false,
                    canEdit: existing?.canEdit || false,
                    canDelete: existing?.canDelete || false
                };
            });
            setFormData(prev => ({ ...prev, permissions: mappedPermissions }));
        }
    }, [modulesList, mode, formData.permissions]); // Added formData.permissions to dependency array

    const validate = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = "Name is required";
        if (!formData.emailId.trim()) newErrors.emailId = "Email is required";
        else if (!/\S+@\S+\.\S+/.test(formData.emailId)) newErrors.emailId = "Invalid email format";
        if (!formData.phoneNumber.trim()) newErrors.phoneNumber = "Phone number is required";
        if (!formData.designation.trim()) newErrors.designation = "Designation is required";
        if (!formData.department.trim()) newErrors.department = "Department is required";
        if (!formData.branchIds?.length) newErrors.branchIds = "Branch is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setIsSubmitting(true);
        const payload = {
            ...formData,
            dateOfBirth: formData.dateOfBirth ? formData.dateOfBirth.toISOString() : null,
            permissions: formData.permissions.map(({ label, ...rest }) => rest),
            profileImage: formData.profileImage?.includes("base64,")
                ? formData.profileImage.split("base64,")[1]
                : formData.profileImage
        };

        if (mode === "edit") {
            payload.teamId = teamId;
        }

        const API_URL = mode === "add" ? ADD_STAFF : UPDATE_STAFF;
        const { statusCode, message } = await postApi(API_URL, payload);

        if (statusCode === 200) {
            successToast(message);
            navigate("/manage-team");
        } else {
            errorToast(message);
        }
        setIsSubmitting(false);
    };

    const handlePermissionChange = (sectionId, field, checked) => {
        setFormData(prev => ({
            ...prev,
            permissions: prev.permissions.map(p =>
                p.sectionId === sectionId ? { ...p, [field]: checked } : p
            )
        }));
    };

    const handleRowAllChange = (sectionId, checked) => {
        setFormData(prev => ({
            ...prev,
            permissions: prev.permissions.map(p =>
                p.sectionId === sectionId
                    ? { ...p, canView: checked, canAdd: checked, canEdit: checked, canDelete: checked }
                    : p
            )
        }));
    };

    const permissionColumns = [
        {
            title: "Module Name",
            dataIndex: "label",
            key: "label"
        },
        {
            title: "All",
            key: "all",
            align: "center",
            render: (_, record) => {
                const allChecked = record.canView && record.canAdd && record.canEdit && record.canDelete;
                const indeterminate = (record.canView || record.canAdd || record.canEdit || record.canDelete) && !allChecked;
                return (
                    <Checkbox
                        checked={allChecked}
                        indeterminate={indeterminate}
                        onChange={(e) => handleRowAllChange(record.sectionId, e.target.checked)}
                    />
                );
            }
        },
        {
            title: "View",
            key: "canView",
            align: "center",
            render: (_, record) => (
                <Checkbox
                    checked={record.canView}
                    onChange={(e) => handlePermissionChange(record.sectionId, "canView", e.target.checked)}
                />
            )
        },
        {
            title: "Add",
            key: "canAdd",
            align: "center",
            render: (_, record) => (
                <Checkbox
                    checked={record.canAdd}
                    onChange={(e) => handlePermissionChange(record.sectionId, "canAdd", e.target.checked)}
                />
            )
        },
        {
            title: "Edit",
            key: "canEdit",
            align: "center",
            render: (_, record) => (
                <Checkbox
                    checked={record.canEdit}
                    onChange={(e) => handlePermissionChange(record.sectionId, "canEdit", e.target.checked)}
                />
            )
        },
        {
            title: "Delete",
            key: "canDelete",
            align: "center",
            render: (_, record) => (
                <Checkbox
                    checked={record.canDelete}
                    onChange={(e) => handlePermissionChange(record.sectionId, "canDelete", e.target.checked)}
                />
            )
        }
    ];

    return (
        <div className="manage-team">
            {isLoading && <Loader />}
            <SubHeader
                title={mode === "add" ? "Add New Staff" : "Edit Staff"}
                showBack={true}
                onBack={() => navigate("/manage-team")}
            />

            <div className="form-container-card">
                <div className="staff-form">
                    <div className="inner-row">
                        <InputField
                            title="Name"
                            placeholder="Enter full name"
                            value={formData.name}
                            required
                            errorText={errors.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        />
                        <InputField
                            title="Email ID"
                            placeholder="Enter email address"
                            value={formData.emailId}
                            required
                            errorText={errors.emailId}
                            onChange={(e) => setFormData(prev => ({ ...prev, emailId: e.target.value }))}
                        />
                    </div>
                    <div className="inner-row">
                        <InputField
                            title="Phone Number"
                            placeholder="Enter phone number"
                            value={formData.phoneNumber}
                            required
                            errorText={errors.phoneNumber}
                            onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                        />
                        <InputField
                            title="Designation"
                            placeholder="Enter designation"
                            value={formData.designation}
                            required
                            errorText={errors.designation}
                            onChange={(e) => setFormData(prev => ({ ...prev, designation: e.target.value }))}
                        />
                    </div>
                    <div className="inner-row">
                        <InputField
                            title="Department"
                            placeholder="Enter department"
                            value={formData.department}
                            required
                            errorText={errors.department}
                            onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                        />
                        <DateField
                            title="Date of Birth"
                            value={formData.dateOfBirth}
                            onChange={(date) => setFormData(prev => ({ ...prev, dateOfBirth: date }))}
                        />
                    </div>
                    <div className="inner-row">
                        <SelectInput
                            title="Gender"
                            placeholder="Select gender"
                            value={formData.gender}
                            options={[
                                { label: "Male", value: "Male" },
                                { label: "Female", value: "Female" },
                                { label: "Other", value: "Other" }
                            ]}
                            onChange={(val) => setFormData(prev => ({ ...prev, gender: val }))}
                        />
                        <SelectInput
                            title="Select Grades"
                            placeholder="Select grades"
                            mode="multiple"
                            value={formData.gradeIds}
                            options={Array.from({ length: 12 }, (_, i) => ({
                                label: `Grade ${i + 1}`,
                                value: i + 1
                            }))}
                            onChange={(val) => setFormData(prev => ({ ...prev, gradeIds: val }))}
                        />
                    </div>
                    <div className="inner-row">
                        <SelectInput
                            title="Select Branch"
                            placeholder="Select branch"
                            mode="multiple"
                            value={formData.branchIds}
                            options={branchesList}
                            required
                            errorText={errors.branchIds}
                            onChange={(val) => setFormData(prev => ({ ...prev, branchIds: val }))}
                        />
                        <ImageUploadField
                            title="Profile Image"
                            value={formData.profileImage}
                            onChange={(_, url) => setFormData(prev => ({ ...prev, profileImage: url }))}
                        />
                    </div>

                    <div className="permissions-section">
                        <h4 className="section-title">Module Permissions</h4>
                        <Table
                            columns={permissionColumns}
                            dataSource={formData.permissions}
                            pagination={false}
                            size="small"
                            bordered
                            rowKey="sectionId"
                        />
                    </div>

                    <div className="form-actions">
                        <ButtonComponent variant="danger" onClick={() => navigate("/manage-team")}>
                            Cancel
                        </ButtonComponent>
                        <ButtonComponent variant="primary" onClick={handleSubmit} loading={isSubmitting}>
                            {mode === "add" ? "Submit" : "Update"}
                        </ButtonComponent>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddEditStaff;
